import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { TRADE_HUB_TRADES, fetchTradeCareerData } from '../utils/tradePayData';
import TradeResourceCard from './TradeResourceCard';
import TradePayDetailCard from './TradePayDetailCard';
import ResourceCardList from './ResourceCardList';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const sectionHeading = {
  fontFamily: FONT_HEAD, fontSize: '1.05rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '10px',
};

// Consolidated single-trade view: schools (grouped by college), unions, licensing/career-path
// content, and real pay data all in one place, so a worker doesn't have to hop between four
// separate tabs to research one trade. Rendered as a full-screen overlay (not a route) so it
// can be opened from any tab in either WorkerDashboard or PublicResources without needing new
// routing -- closing it just clears the parent's state and returns to whatever was underneath.
//
// hubTrade: a trade_resources.trade_name value from TRADE_HUB_TRADES (the 9 canonical trades
// with real licensing content). Each section below only renders when it actually has matching
// data -- no empty "Schools (0)" sections, mirroring the same graceful-degradation pattern
// already built for HVAC's partial pay data.
export default function TradeHub({ hubTrade, onClose }) {
  const { t } = useTranslation();
  const entry = TRADE_HUB_TRADES.find(e => e.hubTrade === hubTrade);
  const [loading, setLoading] = useState(true);
  const [licensing, setLicensing] = useState(null);
  const [schools, setSchools] = useState([]);
  const [unions, setUnions] = useState([]);
  const [payRow, setPayRow] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!hubTrade || !entry) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [licensingRes, schoolsRes, unionsRes, payRows] = await Promise.all([
        supabase.from('trade_resources').select('*').eq('trade_name', entry.hubTrade).eq('is_active', true).maybeSingle(),
        supabase.from('resource_directory').select('*').eq('type', 'school').eq('category', entry.category).eq('is_active', true).order('name', { ascending: true }),
        supabase.from('resource_directory').select('*').eq('type', 'union').eq('category', entry.category).eq('is_active', true).order('name', { ascending: true }),
        fetchTradeCareerData(),
      ]);
      if (cancelled) return;
      setLicensing(licensingRes.data || null);
      setSchools(schoolsRes.data || []);
      setUnions(unionsRes.data || []);
      setPayRow((payRows || []).find(r => r.trade === entry.payTrade) || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubTrade]);

  useEffect(() => {
    // Prevent background scroll while the overlay is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  if (!hubTrade) return null;

  // schools.length counts resource_directory rows (programs), not distinct colleges -- a
  // college with several programs contributes several rows, so the heading needs both numbers
  // or it reads as far more institutions than actually exist.
  const collegeCount = new Set(schools.map(r => r.name)).size;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300, backgroundColor: NAVY,
      overflowY: 'auto', WebkitOverflowScrolling: 'touch',
    }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`,
      }}>
        <div>
          <p style={{ fontSize: '0.68rem', fontWeight: 700, color: AMBER, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '3px' }}>
            {t('resources.tradeHub.eyebrow')}
          </p>
          <h1 style={{ fontFamily: FONT_HEAD, fontSize: '1.5rem', color: WHITE, letterSpacing: '0.03em' }}>
            {hubTrade}
          </h1>
        </div>
        <button
          onClick={onClose}
          aria-label={t('resources.tradeHub.closeAriaLabel')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
            backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
            color: WHITE, fontSize: '18px', cursor: 'pointer', fontFamily: FONT_BODY,
          }}
        >
          ×
        </button>
      </div>

      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '20px', paddingBottom: '60px' }}>
        {!entry && (
          <div style={{ textAlign: 'center', padding: '2.5rem 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>
            {t('resources.tradeHub.notAvailable')}
          </div>
        )}

        {entry && loading && (
          <div style={{ textAlign: 'center', padding: '2.5rem 0', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
            {t('common.loading')}
          </div>
        )}

        {entry && !loading && (
          <>
            {licensing && (
              <div style={{ marginBottom: '28px' }}>
                <p style={sectionHeading}>{t('resources.tradeHub.licensingCareerPath')}</p>
                <TradeResourceCard trade={licensing} />
              </div>
            )}

            {payRow && (
              <div style={{ marginBottom: '28px' }}>
                <p style={sectionHeading}>{t('resources.tradeHub.pay')}</p>
                <TradePayDetailCard row={payRow} isMobile={isMobile} />
              </div>
            )}

            {schools.length > 0 && (
              <div style={{ marginBottom: '28px' }}>
                <p style={sectionHeading}>
                  {t('resources.tradeHub.schoolsAndPrograms')} ({t('resources.collegeCount', { count: collegeCount })}, {t('resources.programCount', { count: schools.length })})
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <ResourceCardList resources={schools} type="school" />
                </div>
              </div>
            )}

            {unions.length > 0 && (
              <div style={{ marginBottom: '28px' }}>
                <p style={sectionHeading}>{t('resources.unionsCount', { count: unions.length })}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <ResourceCardList resources={unions} type="union" />
                </div>
              </div>
            )}

            {!licensing && !payRow && schools.length === 0 && unions.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2.5rem 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>
                {t('resources.tradeHub.noInfo')}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
